import { executeSoon } from '@streamr/tv-utils';
import { hexToBinary, toUserIdRaw, } from '@streamr/utils';
import { StreamrAppApi } from '../../engine/StreamrAppApi';
import { MediaService } from '../media/MediaService';
import { PluginApiService } from '../pluginapi/PluginApiService';
import { PurchaseService } from '../purchase/PurchaseService';
import { VideoPropertiesService } from '../videoProperties/VideoPropertiesService';
import { ViewerCountService } from '../viewercount/ViewerCountService';
import { MultiBroadcasterServiceOutput } from './MultiBroadcasterServiceOutput';
export class MultiBroadcasterService {
    static instance;
    static getInstance() {
        if (!MultiBroadcasterService.instance) {
            MultiBroadcasterService.instance = new MultiBroadcasterService();
        }
        return MultiBroadcasterService.instance;
    }
    static getNotificationInterface() {
        return MultiBroadcasterService.getInstance();
    }
    static getOutput() {
        return MultiBroadcasterServiceOutput.getReactiveOutput();
    }
    // --- Singleton end
    mediaService;
    streamrAppApi;
    output;
    broadcastActivated = false;
    checkStreamOnlineIntervalId;
    currentStreamId;
    lastFrameReceivedAt;
    constructor() {
        this.output = new MultiBroadcasterServiceOutput();
        this.mediaService = MediaService.getInstance();
        this.mediaService.on('mediaStreamChanged', this.onMediaStreamChanged);
        this.lastFrameReceivedAt = 0;
    }
    async init() {
        // No initialization needed
    }
    notifyCanvasMounted(canvas) {
        this.streamrAppApi
            ?.getMediaManager()
            .setCanvas(canvas)
            .catch(() => undefined);
        console.log('streamrAppApi setCanvas called for multiBroadcaster', canvas);
    }
    // Notifications
    notifyActivateBroadcast(activeBroadcast) {
        this.activateBroadcast(activeBroadcast);
    }
    notifyDeactivateBroadcast() {
        this.deactivateBroadcast();
    }
    onFrameReceived() {
        if (!this.output.getWatchingStartedAt()) {
            this.output.setWatchingStartedAt(new Date());
        }
        this.lastFrameReceivedAt = Date.now();
        if (this.checkStreamOnlineIntervalId !== undefined) {
            this.checkStreamOnlineIntervalId = window.setInterval(() => {
                if (Date.now() - this.lastFrameReceivedAt > 5000) {
                    this.output.setIsVideoStreaming(false);
                    this.checkStreamOnlineIntervalId = undefined;
                    clearInterval(this.checkStreamOnlineIntervalId);
                    this.checkStreamOnlineIntervalId = undefined;
                }
            }, 5000);
        }
    }
    createStreamrAppApi(activeBroadcast) {
        const streamrAppApi = new StreamrAppApi({
            streamId: activeBroadcast.streamId,
            broadcasterUserId: toUserIdRaw(activeBroadcast.broadcasterUserId),
            aesKey: hexToBinary(activeBroadcast.aesKey),
        });
        streamrAppApi.getMediaManager().on('muteChange', (muted) => {
            this.output.setAudioMuted(muted);
        });
        streamrAppApi.on('receivedFrame', () => {
            this.onFrameReceived();
        });
        streamrAppApi.on('broadcastMetadataChanged', (broadcastMetadata) => {
            if (this.currentStreamId) {
                ViewerCountService.getInstance().notifyViewerCountChangedForActiveStream(broadcastMetadata.viewerCount);
                PurchaseService.getInstance().notifyProductsOfActiveBroadcastChanged(broadcastMetadata.products, false);
            }
        });
        streamrAppApi
            .getMediaManager()
            .on('videoPropertiesChanged', (videoProperties) => {
            VideoPropertiesService.getNotificationInterface().notifyVideoPropertiesChanged(videoProperties);
        });
        // Set up services when StreamrWrapper is ready
        streamrAppApi.on('streamrWrapperReady', (streamrWrapper) => {
            const pluginApiService = PluginApiService.getInstance();
            pluginApiService.setStreamrWrapper(streamrWrapper);
            const purchaseService = PurchaseService.getInstance();
            purchaseService.setStreamrWrapper(streamrWrapper);
        });
        // Forward plugin messages to PluginApiService
        streamrAppApi.on('pluginMessage', (_message, _publisherId) => {
            // PluginApiService handles this through its StreamrWrapper listener
            // No action needed here since it's already set up in setStreamrWrapper
        });
        // Forward purchase confirmed messages to PurchaseService
        streamrAppApi.on('purchaseConfirmedMessage', (message, _publisherId) => {
            PurchaseService.getInstance().onPurchaseConfirmedMessage(message);
        });
        return streamrAppApi;
    }
    activateBroadcast(activeBroadcast) {
        if (this.currentStreamId === activeBroadcast.streamId) {
            return;
        }
        this.currentStreamId = activeBroadcast.streamId;
        // Stop previous viewer if any
        this.streamrAppApi?.stop().catch(() => undefined);
        this.streamrAppApi = this.createStreamrAppApi(activeBroadcast);
        executeSoon(async () => {
            if (this.streamrAppApi) {
                await this.streamrAppApi.start();
                this.output.setReady(true);
                this.broadcastActivated = true;
                await this.onMediaStreamChanged();
                this.output.setIsVideoStreaming(true);
                await this.streamrAppApi.getMediaManager().unmuteAudio();
                this.output.setAudioMuted(false);
            }
        });
    }
    deactivateBroadcast() {
        executeSoon(async () => {
            await this.streamrAppApi?.getMediaManager().muteAudio();
            this.output.setAudioMuted(true);
            this.output.setReady(false);
            this.output.setIsVideoStreaming(false);
            this.broadcastActivated = false;
            await this.streamrAppApi?.stop();
            this.streamrAppApi = undefined;
            this.currentStreamId = undefined;
            if (this.checkStreamOnlineIntervalId !== undefined) {
                window.clearInterval(this.checkStreamOnlineIntervalId);
                this.checkStreamOnlineIntervalId = undefined;
            }
        });
    }
    onMediaStreamChanged = async () => {
        if (this.broadcastActivated && this.streamrAppApi) {
            // Use source tracks directly (not from combined stream) for Safari compatibility
            const videoTrack = this.mediaService.getSourceVideoTrack();
            if (videoTrack) {
                this.streamrAppApi.getMediaManager().addVideoTrack(videoTrack);
            }
            const audioTrack = this.mediaService.getSourceAudioTrack();
            if (audioTrack) {
                this.streamrAppApi.getMediaManager().addAudioTrack(audioTrack);
            }
        }
    };
}
